import React, { useState } from "react";
import {
  Button,
  CompanyPage,
  FlashMessage,
  SelectNew,
  SingleFileDropZone,
} from "../../../components/common";
import CloudUploadIcon from "../../../assets/svg/cloud-upload-icon.svg";
import marketingSolutionServices from "../../../services/marketingSolution.services";
import {
  SAMPLE_CSV_DOWNLOAD,
  EMAIL_SUBSCRIPTION_OPTIONS,
  SubscriptionStatus,
} from "../../../utils/appConstants";
import { useNavigate } from "react-router-dom";
import appRoutes from "../../../routes/app.routes";
import { useSelector, useDispatch } from "react-redux";
import { rootReducersState } from "../../../reducers";
import {
  setParsedContacts,
  setSubscriptionStatus,
} from "../../../reducers/company/companyMarketingSolution.reducer";

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const UploadContact = () => {
  const [contactFile, setContactFile] = useState<File>();
  const [selectedVal, setSelectedVal] = useState<any>("");
  const navigate = useNavigate();
  const sessionReducer = useSelector(
    ({ session }: rootReducersState) => session
  );
  const companySlug = sessionReducer.currentCompany.slug;
  const dispatch = useDispatch();

  const handleContinue = async () => {
    if (!selectedVal) {
      FlashMessage("Please select an email marketing status", "error");
      return;
    }

    if (!contactFile) {
      FlashMessage("Please upload a CSV file to continue", "error");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", contactFile);
      formData.append("tags", JSON.stringify([1, 2, 3]));

      const result = await marketingSolutionServices.parseContactsFile(formData);

      if (result.flag) {
        dispatch(setParsedContacts(result?.data));
        navigate(appRoutes.createTags.generatePath(companySlug));
      } else {
        FlashMessage(result.message, "error");
      }
    } catch (error: any) {
      FlashMessage(error?.message || "Please upload a valid file", "error");
    }
  };

  const handleSubscriptionStatusChange = (value: any) => {
    setSelectedVal(value);
    const label = value?.label;
    if (label in SubscriptionStatus) {
      dispatch(setSubscriptionStatus(SubscriptionStatus[label]));
    }
  };

  const handleDownload = () => {
    const anchor = document.createElement("a");
    document.body.appendChild(anchor);
    anchor.href = SAMPLE_CSV_DOWNLOAD;
    anchor.download = "Sample File";
    anchor.click();
    window.URL.revokeObjectURL(SAMPLE_CSV_DOWNLOAD);
    anchor.remove();
  };

  const handleUploadFile = (file: File) => {
    if (!file?.name?.toLowerCase().endsWith(".csv")) {
      FlashMessage("Please upload a CSV file", "error");
      return;
    }
    setContactFile(file);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const isReadyToContinue = Boolean(selectedVal && contactFile);

  return (
    <>
      <CompanyPage.Heading
        title="Upload your contacts file"
        subtitle="Import subscribers in bulk, then add tags on the next step."
        extra={
          <SelectNew
            name="email_subscription"
            externalLabel={{ label: "Email marketing status" }}
            placeholder="Select Status"
            options={EMAIL_SUBSCRIPTION_OPTIONS}
            isSearchable={false}
            className="select-box"
            value={selectedVal}
            onChange={handleSubscriptionStatusChange}
          />
        }
      />

      <div className="upload-checklist">
        <span
          className={`upload-checklist__item${
            selectedVal ? " upload-checklist__item--done" : ""
          }`}
        >
          Status selected
        </span>
        <span
          className={`upload-checklist__item${
            contactFile ? " upload-checklist__item--done" : ""
          }`}
        >
          CSV file added
        </span>
      </div>

      <div
        className={`draggable-field${
          contactFile ? " draggable-field--selected" : ""
        }`}
      >
        <SingleFileDropZone
          allowFileTypes={["csv"]}
          allowFileSize={5 * 1024 * 1024}
          onSuccess={handleUploadFile}
          onError={(error) => FlashMessage(error.message, "error")}
          content={
            contactFile ? (
              <div className="upload-csv-content">
                <p>{contactFile.name}</p>
                <p className="small">
                  {formatFileSize(contactFile.size)} · CSV
                </p>
                <p className="small">Click or drop a file to replace</p>
              </div>
            ) : (
              <div className="upload-csv-content">
                <div className="icon">
                  <img src={CloudUploadIcon} alt="" />
                </div>
                <p>Drag or browse a CSV file here to upload</p>
                <p className="small">Upload a .csv file using our sample format</p>
              </div>
            )
          }
        />
      </div>

      <CompanyPage.Footer>
        <CompanyPage.FooterActions>
          <Button type="button" variant="outlined" onClick={handleBack}>
            Back
          </Button>
          <Button
            type="button"
            disabled={!isReadyToContinue}
            onClick={handleContinue}
          >
            Continue
          </Button>
        </CompanyPage.FooterActions>
        <Button type="button" variant="text" onClick={handleDownload}>
          Download Sample File
        </Button>
      </CompanyPage.Footer>
    </>
  );
};

export default UploadContact;
